// Kogusummade kaartide grid (5 kaarti) — port mallist parks_dashboard.py renderTotals()
//
// Kaardid:
//  1. Toodetav võimsus (oranž)
//  2. Võrgu suund (roheline = eksport, punane = import)
//  3. Aku keskmine SOC (lilla) — kui n_batteries > 0
//  4. Lokaalne tarbimine (sinine)
//  5. Toodetud kokku (kollakas)

import { fmt, fmtKWh } from '../../utils/format';

function TotalCard({ klass, iconHref, label, value, unit, sub, children }) {
  return (
    <div className={`total-card ${klass}`}>
      <div className="head">
        <div className="icon">
          <svg>
            <use href={iconHref} />
          </svg>
        </div>
        <div className="lbl">{label}</div>
      </div>
      <div className="val">
        {value}
        {unit && <span className="unit">{unit}</span>}
      </div>
      {children}
      {sub && <div className="sub">{sub}</div>}
    </div>
  );
}

export function TotalsGrid({ total, loading }) {
  // Skeleton — 4 tühja kaarti laadimise ajal
  if (loading || !total) {
    return (
      <div className="totals">
        <div className="skeleton-card" />
        <div className="skeleton-card" />
        <div className="skeleton-card" />
        <div className="skeleton-card" />
      </div>
    );
  }

  const t = total;
  const today = t.today || {};
  const gridIsExport = t.p_grid !== null && t.p_grid !== undefined && t.p_grid < 0;
  const battCharging = t.p_battery !== null && t.p_battery !== undefined && t.p_battery > 0.1;
  const battDischarging = t.p_battery !== null && t.p_battery !== undefined && t.p_battery < -0.1;

  return (
    <div className="totals">
      {/* 1. Toodetav võimsus + täna toodetud kWh */}
      <TotalCard
        klass="solar"
        iconHref="#i-sun"
        label="Toodetav võimsus"
        value={fmt(t.p_solar)}
        unit="kW"
        sub={
          <>
            <span style={{ color: 'var(--positive)' }}>{t.n_online}/{t.n_total} online</span>
            <span style={{ color: 'var(--text-3)' }}>·</span>
            <span>Täna: <strong>{fmtKWh(today.solar_kwh)}</strong></span>
          </>
        }
      />

      {/* 2. Võrgu suund + täna eksport/import */}
      <TotalCard
        klass={gridIsExport ? 'grid-export' : 'grid-import'}
        iconHref={gridIsExport ? '#i-arrow-up' : '#i-arrow-down'}
        label="Võrgu suund"
        value={fmt(Math.abs(t.p_grid ?? 0))}
        unit="kW"
        sub={
          <>
            <span>{gridIsExport ? '↑ Eksport' : '↓ Import'}</span>
            <span style={{ color: 'var(--text-3)' }}>·</span>
            <span>Täna: ↑<strong>{fmtKWh(today.grid_export_kwh)}</strong> ↓<strong>{fmtKWh(today.grid_import_kwh)}</strong></span>
          </>
        }
      />

      {/* 3. Aku keskmine SOC + täna laadis/tühj kWh */}
      {t.n_batteries > 0 && (
        <BatteryTotalCard
          avgSoc={t.avg_soc}
          pBattery={t.p_battery}
          nBatteries={t.n_batteries}
          charging={battCharging}
          discharging={battDischarging}
          todayChg={today.aku_chg_kwh}
          todayDsg={today.aku_dsg_kwh}
        />
      )}

      {/* 4. Lokaalne tarbimine */}
      <TotalCard
        klass="consumption"
        iconHref="#i-house"
        label="Lokaalne tarbimine"
        value={fmt(t.p_consumption)}
        unit="kW"
        sub="Tootmine + võrgu netto − aku"
      />

      {/* 5. Sellel kuul — PEJ tootmine + võrgu eksport/import */}
      <TotalCard
        klass="energy"
        iconHref="#i-spark"
        label="Sellel kuul"
        value={fmtKWh((t.month || {}).solar_kwh)}
        sub={
          <>
            <span>↑<strong>{fmtKWh((t.month || {}).grid_export_kwh)}</strong></span>
            <span style={{ color: 'var(--text-3)' }}>·</span>
            <span>↓<strong>{fmtKWh((t.month || {}).grid_import_kwh)}</strong></span>
          </>
        }
      />

      {/* 6. Sellel aastal */}
      <TotalCard
        klass="energy"
        iconHref="#i-spark"
        label="Sellel aastal"
        value={fmtKWh((t.year || {}).solar_kwh)}
        sub={
          <>
            <span>↑<strong>{fmtKWh((t.year || {}).grid_export_kwh)}</strong></span>
            <span style={{ color: 'var(--text-3)' }}>·</span>
            <span>↓<strong>{fmtKWh((t.year || {}).grid_import_kwh)}</strong></span>
          </>
        }
      />
    </div>
  );
}

function BatteryTotalCard({ avgSoc, pBattery, nBatteries, charging, discharging, todayChg, todayDsg }) {
  const stateLabel = charging ? 'Laeb' : discharging ? 'Tühjeneb' : 'Standby';
  const stateClass = charging ? 'charge' : discharging ? 'discharge' : 'standby';
  const soc = Math.max(0, Math.min(100, avgSoc || 0));

  return (
    <div className="total-card battery">
      <div className="head">
        <div className="icon">
          <svg>
            <use href="#i-battery" />
          </svg>
        </div>
        <div className="lbl">Aku keskmine SOC</div>
      </div>
      <div className="val">
        {fmt(avgSoc, 1)}
        <span className="unit">%</span>
      </div>
      <div className="mini-soc">
        <div className="mini-soc-fill" style={{ width: `${soc}%` }} />
      </div>
      <div className="sub">
        <span className={`batt-stat ${stateClass}`}>
          <span className="dot" />
          {stateLabel}
        </span>
        <span>
          Täna: ↑<strong>{fmtKWh(todayChg)}</strong> ↓<strong>{fmtKWh(todayDsg)}</strong>
        </span>
      </div>
    </div>
  );
}

export default TotalsGrid;
